import type { CardFormat } from '@/features/dashboard/api/schemas'
import { tickLabel, type Axis } from '@/features/dashboard/format/axis'
import {
  currencySymbol,
  formatAbbrev,
} from '@/features/dashboard/format/format'

/** The width assumed where the chart has not been measured yet (first render, jsdom). */
export const DEFAULT_WIDTH = 640

/** The plot's width: what the y axis labels and the padding leave of the chart's. */
export const plotWidth = (width: number) =>
  Math.max((width > 0 ? width : DEFAULT_WIDTH) - 72, 120)

/** The room one x label needs, in px, before its neighbours are thinned out. */
const X_LABEL_PX = 56

/**
 * Every how-manyth category carries its label: 1 while they fit, else the
 * smallest step that leaves each label its room. Counted back from the latest
 * category, so that the latest is always labelled (as the Figma tiles do).
 */
export const labelStride = (count: number, width: number) =>
  Math.max(1, Math.ceil((count * X_LABEL_PX) / plotWidth(width)))

/** 'Jan 2025' -> 'Jan 25': the axis' short month, the tooltip keeps the year in full. */
export const shortMonth = (label: string) =>
  label.replace(/^([A-Z][a-z]{2}) 20(\d{2})$/, '$1 $2')

/** The round step (1, 2, 2.5, 5 times a power of ten) that is at least `rough`. */
const niceStep = (rough: number) => {
  const power = 10 ** Math.floor(Math.log10(rough))
  return (
    power * ([1, 2, 2.5, 5, 10].find(factor => factor * power >= rough) ?? 10)
  )
}

/**
 * An axis from `lo` to `hi` with at least `ticks` intervals, in round steps:
 * the right-hand axis of a dual chart uses the left one's count, so that their
 * gridlines meet.
 */
export const axisWithTicks = (lo: number, hi: number, ticks: number): Axis => {
  const span = hi - lo
  if (span === 0) {
    const pad = Math.max(Math.abs(lo) * 0.1, 1)
    return axisWithTicks(lo - pad, hi + pad, ticks)
  }
  if (!(span > 0)) return { min: lo, max: lo + ticks, ticks }
  const step = niceStep(span / ticks)
  const min = Math.floor(lo / step) * step
  const count = Math.max(ticks, Math.ceil((hi - min) / step))
  return { min, max: min + step * count, ticks: count }
}

/** Room kept beyond the longest column, as a share of the range, for its value. */
const VALUE_MARGIN = 0.14
const WANTED_TICKS = 5

/** The axis of delta columns: zero and the columns, with room for their values, zero on a tick. */
export const paddedRange = (values: number[]): Axis => {
  const low = Math.min(0, ...values)
  const high = Math.max(0, ...values)
  const span = high - low
  if (span === 0) return { min: 0, max: 1, ticks: 1 }
  const step = niceStep((span * (1 + 2 * VALUE_MARGIN)) / WANTED_TICKS)
  const min =
    low < 0 ? Math.floor((low - span * VALUE_MARGIN) / step) * step : 0
  const max =
    high > 0 ? Math.ceil((high + span * VALUE_MARGIN) / step) * step : 0
  return { min, max, ticks: Math.round((max - min) / step) }
}

/** A value under a thousand keeps the decimals it has (1.2, 0.5), up to `max`. */
const small = (value: number, unit: string | null | undefined, max: number) => {
  const symbol = currencySymbol(unit) ?? ''
  const text = Number(Math.abs(value).toFixed(max)).toString()
  return `${value < 0 ? '-' : ''}${symbol}${text}`
}

/** A tick: '600K', '1.25M', '0', or a percentage for a percent card. */
export const tick = (
  value: number,
  unit: string | null | undefined,
  format: CardFormat | undefined
) => {
  if (format?.style === 'percent') return tickLabel(value, format)
  if (Math.abs(value) < 1000) return small(value, unit, 2)
  const label = formatAbbrev(value, unit, 4)
  return label.includes('.')
    ? label.replace(/0+(?=[A-Z]?$)/, '').replace(/\.(?=[A-Z]?$)/, '')
    : label
}

/**
 * A total above a column, as short as it can be while telling the columns
 * apart: no decimals from 100 up, one below.
 */
export const compactValue = (
  value: number,
  unit: string | null | undefined,
  format: CardFormat | undefined
) => {
  if (format?.style === 'percent') {
    return `${Number((value * 100).toFixed(1))}%`
  }
  if (Math.abs(value) < 1000) return small(value, unit, 1)
  const text = formatAbbrev(value, unit, 1)
  // '45.0M' -> '45M'; '1.2M' stays.
  return text.replace(/\.0(?=[A-Za-z]?$)/, '')
}

/** A signed value for a delta column: '+36K', '-58K'. */
export const signedValue = (
  value: number,
  unit: string | null | undefined,
  format: CardFormat | undefined
) => {
  const text = compactValue(value, unit, format)
  return value > 0 ? `+${text}` : text
}

/**
 * A name broken into lines of at most `chars` characters (the last cut short
 * with an ellipsis past `maxLines`), for an axis whose names would otherwise be
 * rotated or cropped.
 */
export const wrapLabel = (label: string, chars: number, maxLines = 3) => {
  const lines: string[] = []
  let line = ''
  for (const word of label.split(' ')) {
    const next = line === '' ? word : `${line} ${word}`
    if (next.length > chars && line !== '') {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  }
  if (line !== '') lines.push(line)
  if (lines.length <= maxLines) return lines
  const kept = lines.slice(0, maxLines)
  kept[maxLines - 1] =
    `${kept[maxLines - 1]?.slice(0, Math.max(chars - 1, 1))}…`
  return kept
}
